import { Button } from "@optimizely/axiom";
import {
    ADMINCENTER_CUSTOM_GROUP_ACCESS_REVIEW_EVENT_NAME,
    ALL_PROJECTS_ID,
    ALL_PROJECTS_NAME,
    ATTRIBUTE_ROLES,
    GROUP_TYPES
} from "../../../../constants/constants";
import LimitByRole from "../../LimitByRole/LimitByRole";
import { AccessDetailsTable } from "../AccessDetailsTable/AccessDetailsTable";
import { AccessManagementUserFormAccessHeader } from "../AccessManagementUserForm/AccessManagementUserFormAccessHeader";
import { AddProductAccessPermission, AddProductAccessForm } from "../AddProductAccess/AddProductAccessForm";
import { useEffect, useMemo, useRef, useState } from "react";
import { useAccessFlowUserFormContext } from "../AccessManagementUserForm/AccessManagementUserFormProvider";
import { AddGroupAccessForm } from "../AddGroupAccess/AddGroupAccessForm";
import { CustomGroupAccessConfirmationModal } from "../CustomGroupAccessConfirmation/CustomGroupAccessConfirmationModal";
import {
    addGroupWithoutProductAccess,
    convertAccessToAccessDetailsByProductId,
    grantsNoProductAccess,
    UserGroupWithProjectDetails
} from "../../../../lib/access-helpers";
import { GroupsWithoutProductAccessList } from "../GroupsWithoutProductAccess/GroupsWithoutProductAccessList";
import { AccessDetailsByProductId, AccessDetailsRow } from "../../../../domain/AccessDetails";
import { User } from "../../../../domain/User";
import { useUserAccessList } from "../../../../hooks/useUserAccessList/useUserAccessList";
import { useUserContext } from "../../../../providers/UserProvider";

const cloneAccessDetailsByProductId = (details?: AccessDetailsByProductId): AccessDetailsByProductId => {
    if (!details) {
        return {};
    }

    if (typeof structuredClone === "function") {
        return structuredClone(details);
    }

    return JSON.parse(JSON.stringify(details)) as AccessDetailsByProductId;
};

// Re-export for backward compatibility
export type { UserGroupWithProjectDetails };

export const InvitationAccessForm = ({
    className,
    email,
    organizationId,
    selectedUser,
    onAccessDetailsChange,
    onGroupsWithoutProductAccessChange,
    initialAccessDetails,
    initialGroupsWithoutProductAccess
}: {
    className?: string;
    email: string | null;
    organizationId: string | undefined;
    selectedUser: User | undefined;
    onAccessDetailsChange?: (accessDetails: AccessDetailsByProductId) => void;
    onGroupsWithoutProductAccessChange?: (groups: UserGroupWithProjectDetails[]) => void;
    initialAccessDetails?: AccessDetailsByProductId;
    initialGroupsWithoutProductAccess?: UserGroupWithProjectDetails[];
}) => {
    const { accessContext } = useUserContext();
    const [accessFormType, setAccessFormType] = useState<"Product" | "Custom Group" | undefined>(undefined);
    const [accessDetailsByProductId, setAccessDetailsByProductId] = useState<AccessDetailsByProductId>(() =>
        cloneAccessDetailsByProductId(initialAccessDetails)
    );
    // Groups that grant no product access cannot be held in accessDetailsByProductId, which is
    // keyed by product id. Tracking them here keeps them part of the selection (EPTS-6429).
    const [groupsWithoutProductAccess, setGroupsWithoutProductAccess] = useState<UserGroupWithProjectDetails[]>(
        () => initialGroupsWithoutProductAccess ?? []
    );
    const { updateCustomGroupAccess, setUpdateCustomGroupAccess } = useAccessFlowUserFormContext();

    // Fetch existing user access if a user is selected (for existing users)
    const { accessList: existingUserAccess, isLoading: isLoadingUserAccess } = useUserAccessList({
        organizationId,
        user: selectedUser,
        context: accessContext
    });
    const existingAccessByProduct = useMemo(
        () =>
            selectedUser && !isLoadingUserAccess
                ? convertAccessToAccessDetailsByProductId(existingUserAccess || [])
                : {},
        [existingUserAccess, selectedUser, isLoadingUserAccess]
    );
    const mergedExistingAccess = useMemo(
        () => ({ ...existingAccessByProduct, ...accessDetailsByProductId }),
        [existingAccessByProduct, accessDetailsByProductId]
    );

    const prevSelectedUserRef = useRef<User | undefined>();
    useEffect(() => {
        if (prevSelectedUserRef.current && (!selectedUser || prevSelectedUserRef.current.id !== selectedUser.id)) {
            setAccessDetailsByProductId({});
            setGroupsWithoutProductAccess([]);
            setAccessFormType(undefined);
        }
        prevSelectedUserRef.current = selectedUser;
    }, [selectedUser]);

    const prevEmailRef = useRef<string | null>(email);
    useEffect(() => {
        if (!selectedUser && prevEmailRef.current !== email && email !== null) {
            setAccessFormType(undefined);
        }
        prevEmailRef.current = email;
    }, [email, selectedUser]);

    useEffect(() => {
        const handleEvent = (e: CustomEvent) => {
            const { action, userGroup } = e.detail;
            setUpdateCustomGroupAccess({ action, id: userGroup.id, name: userGroup.name, userGroup });
        };

        window.addEventListener(
            ADMINCENTER_CUSTOM_GROUP_ACCESS_REVIEW_EVENT_NAME,
            handleEvent as EventListenerOrEventListenerObject
        );

        return () => {
            window.removeEventListener(
                ADMINCENTER_CUSTOM_GROUP_ACCESS_REVIEW_EVENT_NAME,
                handleEvent as EventListenerOrEventListenerObject
            );
        };
    }, [setUpdateCustomGroupAccess]);

    useEffect(() => {
        if (onAccessDetailsChange) {
            onAccessDetailsChange(accessDetailsByProductId);
        }
    }, [accessDetailsByProductId, onAccessDetailsChange]);

    useEffect(() => {
        if (onGroupsWithoutProductAccessChange) {
            onGroupsWithoutProductAccessChange(groupsWithoutProductAccess);
        }
    }, [groupsWithoutProductAccess, onGroupsWithoutProductAccessChange]);

    const handleSave = ({ permissions }: { permissions: AddProductAccessPermission[] }) => {
        if (!permissions || permissions.length === 0) {
            return;
        }

        const updatedAccessDetailsByProductId = { ...accessDetailsByProductId };

        permissions.forEach((permission) => {
            if (!permission || !permission.product || !permission.instance) {
                return;
            }

            const { product, instance, group, role, project } = permission;

            const productId = product.id;
            const instanceId = instance.id;

            // Get the existing ProductAccess object or create a new one
            const productAccess = updatedAccessDetailsByProductId[productId] || {
                productName: product.name,
                instanceAccess: {}
            };

            // Get the existing AccessDetailsRow array or create a new one
            const accessDetailRows = productAccess.instanceAccess[instanceId] || [];

            // Always create a new AccessDetailsRow for each role
            const newInstanceRow: AccessDetailsRow = {
                instanceName: instance.name,
                roles: [role], // Single role per AccessDetailsRow
                nickname: instance.nickname,
                projectId: role.isInstanceRole ? ALL_PROJECTS_ID : project?.id,
                projectName: role.isInstanceRole ? ALL_PROJECTS_NAME : project?.name,
                groupId: group.id,
                groupName: group.name,
                isCustomGroup: group.groupType === GROUP_TYPES.CUSTOM
            };

            // Add the new AccessDetailsRow to the accessDetailsRows array
            accessDetailRows.push(newInstanceRow);

            // Assign the updated rows back to the product access
            productAccess.instanceAccess[instanceId] = accessDetailRows;

            // Set the updated ProductAccess in the accessDetailsByProductId object
            updatedAccessDetailsByProductId[productId] = productAccess;
        });

        // Update the state with the modified accessDetailsByProductId
        setAccessDetailsByProductId(updatedAccessDetailsByProductId);
        closeAccessForm();
    };

    const handleRemove = ({ accessDetailsRow }: { accessDetailsRow: AccessDetailsRow }) => {
        setAccessDetailsByProductId((prevDetails) => {
            // Create a copy of the current accessDetails
            const updatedDetails = { ...prevDetails };

            // Iterate through all productAccess entries
            for (const productId in updatedDetails) {
                const productAccess = updatedDetails[productId];

                // Iterate through all instanceAccess entries
                for (const instanceId in productAccess.instanceAccess) {
                    const accessDetailsRows = productAccess.instanceAccess[instanceId];

                    // Find the index of the accessDetailsRow to remove by matching groupId
                    const rowIndex = accessDetailsRows.findIndex((row) => row.groupId === accessDetailsRow.groupId);

                    if (rowIndex >= 0) {
                        // Remove the specific AccessDetailsRow
                        accessDetailsRows.splice(rowIndex, 1);

                        // If no rows are left, remove the accessDetail entry
                        if (accessDetailsRows.length === 0) {
                            delete productAccess.instanceAccess[instanceId];
                        }
                    }
                }

                // If no instanceAccess entries are left, remove the productAccess entry
                if (Object.keys(productAccess.instanceAccess).length === 0) {
                    delete updatedDetails[productId];
                }
            }

            return updatedDetails;
        });
    };

    const closeAccessForm = () => {
        setAccessFormType(undefined);
    };

    const submitAddCustomGroupAccess = async () => {
        const customGroupAccessDetails = updateCustomGroupAccess?.accessDetails;

        // The confirmation modal publishes an empty accessDetails map while it is still fetching
        // the group's product instances, and again if that fetch fails. Both are indistinguishable
        // from a genuinely product-less group, so classify only once the lookup has succeeded.
        // The modal's CTA is disabled until then; this is the backstop (EPTS-6429).
        if (updateCustomGroupAccess?.accessDetailsStatus !== "loaded") {
            return;
        }

        // A group with no product instances contributes no product key, so the merge below is a
        // no-op for it. Track it separately instead of losing the selection entirely (EPTS-6429).
        if (grantsNoProductAccess(customGroupAccessDetails)) {
            setGroupsWithoutProductAccess((prev) =>
                addGroupWithoutProductAccess(prev, updateCustomGroupAccess?.userGroup)
            );
            setUpdateCustomGroupAccess(undefined);
            setAccessFormType(undefined);
            return;
        }

        if (!!customGroupAccessDetails) {
            setAccessDetailsByProductId((tableAccessDetailsByProductId) => {
                const tableAccess = { ...tableAccessDetailsByProductId };
                // iterate over each productId in the selected custom group
                Object.keys(customGroupAccessDetails).forEach((productId) => {
                    // merge custom group and previous table product access
                    if (!tableAccess[productId]) {
                        tableAccess[productId] = customGroupAccessDetails[productId];
                    } else {
                        const tableInstanceAccess = tableAccess[productId]?.instanceAccess;
                        // iterate over each instanceId of the already listed product in the selected custom group
                        Object.keys(customGroupAccessDetails[productId].instanceAccess).forEach((instanceId) => {
                            // merge custom group and previous table instance access
                            if (!tableInstanceAccess[instanceId]) {
                                tableInstanceAccess[instanceId] =
                                    customGroupAccessDetails[productId].instanceAccess[instanceId];
                            } else {
                                tableInstanceAccess[instanceId] = [
                                    ...tableInstanceAccess[instanceId],
                                    ...customGroupAccessDetails[productId].instanceAccess[instanceId]
                                ];
                            }
                        });
                    }
                });
                return tableAccess;
            });
        }

        setUpdateCustomGroupAccess(undefined);
        setAccessFormType(undefined);
    };

    const cancelAddCustomGroupAccess = () => {
        setUpdateCustomGroupAccess(undefined);
    };

    return (
        <div {...(className && { className })}>
            <AccessManagementUserFormAccessHeader>
                <LimitByRole action={[ATTRIBUTE_ROLES.GROUPS.UPDATE, ATTRIBUTE_ROLES.GROUPS.MANAGE]} mode="hide">
                    <div className="flex justify-content-between align-items-center">
                        <Button
                            className="push--right"
                            // eslint-disable-next-line react/style-prop-object
                            style="outline"
                            isDisabled={!!accessFormType || !email}
                            whiteBackground
                            onClick={() => setAccessFormType("Product")}
                        >
                            Add Product Access
                        </Button>
                        <LimitByRole action={[ATTRIBUTE_ROLES.GROUPS.UPDATE]} mode="hide">
                            <Button
                                className="push--right"
                                // eslint-disable-next-line react/style-prop-object
                                style="outline"
                                isDisabled={!!accessFormType || !email}
                                whiteBackground
                                onClick={() => setAccessFormType("Custom Group")}
                            >
                                Add Group Access
                            </Button>
                        </LimitByRole>
                    </div>
                </LimitByRole>
            </AccessManagementUserFormAccessHeader>
            {accessFormType === "Product" && (
                <AddProductAccessForm
                    existingAccess={mergedExistingAccess}
                    className="push-double--top push-double--bottom"
                    onSubmit={handleSave}
                    onClose={closeAccessForm}
                    isSaving={false}
                />
            )}
            {accessFormType === "Custom Group" && (
                <AddGroupAccessForm
                    onClose={closeAccessForm}
                    organizationId={organizationId}
                    email={email || ""}
                    customGroups
                />
            )}
            {!!updateCustomGroupAccess && (
                <CustomGroupAccessConfirmationModal
                    action={updateCustomGroupAccess.action}
                    errorMessage=""
                    // submitAddCustomGroupAccess classifies on accessDetails, so confirming before
                    // the lookup resolves would record the wrong thing (EPTS-6429).
                    isConfirmDisabled={updateCustomGroupAccess.accessDetailsStatus !== "loaded"}
                    isSaving={false}
                    userGroup={updateCustomGroupAccess.userGroup!}
                    onClose={cancelAddCustomGroupAccess}
                    onSubmit={submitAddCustomGroupAccess}
                />
            )}
            <AccessDetailsTable accessDetails={accessDetailsByProductId} onRemove={handleRemove} />
            <GroupsWithoutProductAccessList
                className="push--top"
                groups={groupsWithoutProductAccess}
                onRemove={(group) => setGroupsWithoutProductAccess((prev) => prev.filter((g) => g.id !== group.id))}
            />
        </div>
    );
};

InvitationAccessForm.displayName = "InvitationAccessForm";
